import { Component, Event, EventEmitter, forceUpdate, h, Listen, Prop, State, Watch } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import {
    addChat,
    ChatServiceError,
    ErrorDispatcher,
    fetchChats,
    getCaseStatusString,
    IChatData,
    IError,
    NetworkError,
    NotFoundError
} from "../../backend/chatService";
import loadTranslations, { TranslationKey, TranslationOptions } from "../../utils/loadTranslations";
import { debug } from "../../utils/utils";
import { EnvConfig, getEnvConfig, setEnv } from "../../config/env";
import { getAuthClient } from "@trumpf/ux.utils";
import { authClientName } from "../../config/authConfig";
import { ISidebarItem } from "./sis-sidebar/sis-sidebar";
import authStore from "../../store/authStore";

@Component({
    tag: "ai-chat-wrapper",
    styleUrl: "ai-chat-wrapper.scss",
    shadow: true
})
export class AiChatWrapper {
    private caseStatusInterval: NodeJS.Timeout | null = null;
    private errorListener: ((error: IError) => void) | null = null;
    private handleOnlineStatusChange!: () => void;
    private handleGlobalError!: (error: IError) => void;
    private handleResize!: () => void;
    private i18nSubscriberId: string;
    private initialized = false;

    /** The unique identifier of the case */
    @Prop() readonly caseId!: string;

    /** Authentication token for API requests */
    @Prop({ mutable: true }) token?: string;

    /** environment name to choose the correct config */
    @Prop() readonly env: "prod" | "qa" | "dev" = "prod";

    /** Current locale for translations */
    @Prop({ reflect: true, mutable: true }) locale: string;

    /** Enable debug logging - automatically true for qa/dev, false for prod */
    @State() debug = false;

    /** Machine name (e.g. "TruLaser1030 (L26)") */
    @Prop() readonly machineName?: string;

    /** Machine number identifier */
    @Prop() readonly machineNumber?: string;

    /** Software type (e.g. "TruTops Boost") */
    @Prop() readonly softwareType?: string;

    /** Software module (e.g. "HomeZone") */
    @Prop() readonly softwareModule?: string;

    /** Error description - free text field describing customer problem */
    @Prop() readonly errorDescription?: string;

    /** Error number(s) linked to the case during creation */
    @Prop() readonly errorNumbers?: string;

    /** Show sidebar for multi-chat functionality */
    @Prop() readonly showSidebar = false;

    @State() chat: IChatData | null = null;
    @State() caseStatus = "loading";
    @State() loading = true;
    @State() error: IError | null = null;
    @State() retryCount = 0;
    @State() isOffline = !navigator.onLine;
    @State() isMobile: boolean = (window as Window).innerWidth <= 768;
    @State() hasAcceptedDisclaimer = false;
    @State() translationsLoaded = false;
    @State() isServiceApp = false;
    @State() activeItemId = "";
    @State() sidebarItems: ISidebarItem[] = [];
    @State() availableChats: IChatData[] = [];

    private config: EnvConfig;

    /** Event emitted when the case status changes */
    @Event() caseStatusUpdated: EventEmitter<string>;

    constructor() {
        this.handleOnlineStatusChange = this.handleOnlineStatusChangeImpl.bind(this) as () => void;
        this.handleGlobalError = this.handleGlobalErrorImpl.bind(this) as (error: IError) => void;
        this.handleResize = this.handleResizeImpl.bind(this) as () => void;
        this.errorListener = this.handleGlobalError;
        this.setEnvConfig();
    }

    @Watch("env")
    private setEnvConfig() {
        setEnv(this.env);
        this.config = getEnvConfig(this.env);
    }

    /**
     * Listens for the tokenReady event emitted by the auth-handler component.
     * This is the primary trigger for initialization when not in service app mode.
     */
    @Listen("tokenReady", { target: "window" }) // Listen on window to catch event from auth-handler
    handleTokenReady(event: CustomEvent<string>) {
        const token = event.detail;
        debug.log(this, `Received tokenReady event`, {
            hasToken: !!token,
            isServiceApp: this.isServiceApp,
            isInitialized: this.initialized
        });

        // Only initialize if we have a token, aren't in service app mode, and haven't initialized yet
        if (token && !this.isServiceApp && !this.initialized) {
            debug.log(this, "tokenReady condition met. Calling init()...");
            this.initialized = true; // Set initialized flag before calling init
            this.token = token; // Store the token received from the event
            this.init();
        }
    }

    private handleOnlineStatusChangeImpl() {
        this.isOffline = !navigator.onLine;
        if (this.isOffline) {
            this.error = {
                errorCode: 503,
                errorMessage: i18nProvider.t("ai-in-service:error.offline")
            };
            this.loading = false;
        } else {
            this.error = null;
            void this.retryFailedOperations();
        }
    }

    private handleGlobalErrorImpl(error: IError) {
        debug.log(this, "Setting global error:", error);
        this.error = error;
    }

    private handleResizeImpl() {
        this.isMobile = (window as Window).innerWidth <= 768;
    }

    private handleSidebarItemSelect = (id: string) => {
        if (id === "new-chat") {
            // Handle new chat creation
            debug.log(this, "Create new chat triggered");
            void this.createNewChat();
            return;
        }

        this.activeItemId = id;
        debug.log(this, `Setting active chat ID to: ${id}`);

        // First set the currently cached version for immediate display
        const selectedChat = this.availableChats.find((chat) => chat.sessionId === id);
        if (selectedChat) {
            this.chat = selectedChat;
        }

        // Then fetch the latest version of this chat to ensure we have up-to-date data
        void this.fetchAndUpdateChat(id);
    };

    private async fetchAndUpdateChat(sessionId: string) {
        debug.log(this, `Fetching latest data for chat: ${sessionId}`);
        try {
            // Show loading state but keep the current chat visible
            this.loading = true;

            // Pass middleware params to fetchChats
            const middlewareParams = this.getMiddlewareParams();

            // Fetch all chats for the case to get the latest data
            const chats = await fetchChats(this.caseId, middlewareParams);
            debug.log(this, `Received ${chats.length} chats for caseId: ${this.caseId}`);

            // Find the specific chat we're interested in
            const updatedChat = chats.find((chat) => chat.sessionId === sessionId);

            if (updatedChat) {
                // Update the specific chat in our local cache
                this.availableChats = chats;
                this.updateSidebarItems();

                // Set the updated chat as current
                this.chat = updatedChat;
                debug.log(this, `Updated chat with fresh data: ${sessionId}, messages: ${updatedChat.messages?.length || 0}`);
            } else {
                debug.log(this, `Warning: Chat with ID ${sessionId} no longer exists in the latest data`);
                // The selected chat no longer exists, select the first available one
                if (chats.length > 0) {
                    this.chat = chats[0];
                    this.activeItemId = chats[0].sessionId;
                    debug.log(this, `Falling back to first available chat: ${this.chat.sessionId}`);
                }
            }
        } catch (error) {
            console.error(`[AiChatWrapper:fetchAndUpdateChat] Error fetching chat ${sessionId}:`, error);
            // Don't show an error modal for this, just log it
            debug.log(this, `Failed to fetch updated chat data: ${error.message}`);
        } finally {
            this.loading = false;
        }
    }

    private async createNewChat() {
        debug.log(this, "Creating a new chat");
        try {
            this.loading = true;
            // Default to User group if not specified
            const group = "User";
            const author = "User";

            // Create a new chat
            const newChat = await addChat(this.caseId, author, group);
            debug.log(this, "New chat created:", newChat);

            // Add empty messages array if not present
            if (!newChat.messages) {
                newChat.messages = [];
            }

            // Update the available chats and set the new chat as active
            this.availableChats = [newChat, ...this.availableChats];
            this.chat = newChat;
            this.activeItemId = newChat.sessionId;
            this.updateSidebarItems();

            // Reset error state
            this.error = null;
        } catch (error) {
            console.error("[AiChatWrapper:createNewChat] Error creating new chat:", error);
            this.error = {
                errorCode: 500,
                errorMessage: i18nProvider.t("ai-in-service:error.createChat")
            };
        } finally {
            this.loading = false;
        }
    }

    private updateSidebarItems() {
        this.sidebarItems = this.availableChats.map((chat) => {
            // Get the latest message or use a placeholder
            const latestMessage =
                chat.messages && chat.messages.length > 0 ? chat.messages[chat.messages.length - 1].message : "New conversation";

            // Truncate the message if it's too long
            const title = latestMessage.length > 37 ? `${latestMessage.substring(0, 37)}...` : latestMessage;

            // Format the date
            const date = new Date(chat.timestamp);
            const formattedDate = `${date.toLocaleDateString(this.locale)}`;

            return {
                id: chat.sessionId,
                title: title,
                subtitle: chat.author || chat.group,
                date: formattedDate
            };
        });
    }

    async componentWillLoad() {
        // Get initial locale from i18nProvider
        const { language } = i18nProvider;
        this.locale = language;
        this.debug = this.env === "qa" || this.env === "dev";
        await this.loadTranslations();

        window.addEventListener("online", this.handleOnlineStatusChange);
        window.addEventListener("offline", this.handleOnlineStatusChange);
        window.addEventListener("resize", this.handleResize);
        ErrorDispatcher.addEventListener(this.errorListener);

        // Check if token is provided directly (Service App Mode)
        if (this.token) {
            this.isServiceApp = true;
            debug.log(this, "Service app mode enabled - token provided directly");
            // Ensure the auth client and store reflect this token
            getAuthClient(authClientName).setAccessToken(this.token);
            authStore.setToken(this.token);
            this.initialized = true;
            this.init();
        } else {
            debug.log(this, "Standard mode - waiting for tokenReady event...");
            // No immediate token check here, rely on the @Listen('tokenReady')
        }

        // Subscribe to i18n changes
        this.i18nSubscriberId = i18nProvider.subscribe(() => {
            this.translationsLoaded = true;
            forceUpdate(this);
        });
    }

    connectedCallback() {
        debug.log(this, "connectedCallback fired.");
        // Removed token checking logic
    }

    disconnectedCallback() {
        this.initialized = false; // Reset initialized flag when component is disconnected
        debug.log(this, "disconnectedCallback fired.");

        this.stopCaseStatusSubscription();
        window.removeEventListener("online", this.handleOnlineStatusChange);
        window.removeEventListener("offline", this.handleOnlineStatusChange);
        window.removeEventListener("resize", this.handleResize);
        if (this.errorListener) {
            ErrorDispatcher.removeEventListener(this.errorListener);
        }
        if (this.i18nSubscriberId) {
            i18nProvider.unsubscribe(this.i18nSubscriberId);
        }
    }

    @Watch("locale")
    async onLocaleChange() {
        const currentLanguage = i18nProvider.language;
        // Only update if the base language part is different
        const currentBase = currentLanguage.split("-")[0];
        const newBase = this.locale.split("-")[0];

        if (currentBase !== newBase) {
            await this.loadTranslations();
        }
    }

    private async loadTranslations() {
        try {
            const locale = await loadTranslations(this.locale);
            await i18nProvider.changeLanguage(locale);
            // Don't update this.locale here to prevent loops
            this.translationsLoaded = true;
        } catch (error) {
            console.error("Failed to load or change translations:", error);
        }
    }

    private async retryFailedOperations() {
        if (this.error) {
            this.error = null;
            this.retryCount++;
            debug.log(this, `Retry attempt #${this.retryCount} started...`);
            try {
                await this.loadChat();
                await this.loadCaseStatus();
            } catch (error) {
                debug.log(this, `Retry attempt #${this.retryCount} failed:`, error);
                // Error will be handled by the respective methods
            }
        }
    }

    private async init() {
        // Ensure init only runs once
        if (!this.initialized) {
            // debug.log(this, "Attempted to call init() before initialized flag set. Aborting."); // Less critical
            return;
        }

        // debug.log(this, "Entering init() function."); // Removed

        // Clear any previous error
        this.error = null;

        const initTimeout = setTimeout(() => {
            debug.log(this, "Init process timed out");
            if (this.loading) {
                this.error = {
                    errorCode: 408,
                    errorMessage: i18nProvider.t("ai-in-service:error.timeout") || "Connection timed out. Please try again."
                };
                this.loading = false;
            }
        }, 15000);

        // Token check is now handled by the @Listen or service app logic

        try {
            this.loading = true; // Ensure loading state is set at the beginning of init
            // debug.log(this, "Starting init process with loadChat()"); // Removed
            await this.loadChat();
            await this.loadCaseStatus();
            this.startCaseStatusSubscription();
        } catch (error) {
            debug.log(this, "Error in init:", error);
            this.stopCaseStatusSubscription();
            // Errors already handled by the respective methods
        } finally {
            clearTimeout(initTimeout);
            // Loading is set to false within loadChat/loadCaseStatus or timeout
        }
    }

    private getMiddlewareParams() {
        const middlewareParams = {
            machineName: this.machineName,
            machineNumber: this.machineNumber,
            softwareType: this.softwareType,
            softwareModule: this.softwareModule,
            errorDescription: this.errorDescription,
            errorNumbers: this.errorNumbers
        };

        // Only include non-undefined properties in the params
        return Object.fromEntries(Object.entries(middlewareParams).filter(([, value]) => value !== undefined));
    }

    private async loadChat() {
        // debug.log(this, `Starting to load chat with caseId: ${this.caseId}`); // Removed

        if (!this.caseId) {
            console.error("[AiChatWrapper:loadChat] No caseId provided");
            this.error = {
                errorCode: 400,
                errorMessage: i18nProvider.t("ai-in-service:error.caseIdRequired")
            };
            this.stopCaseStatusSubscription();
            this.loading = false; // Ensure loading stops
            return;
        }

        try {
            // Ensure loading is true at the start
            if (!this.loading) this.loading = true;

            // Pass middleware params to fetchChats
            const middlewareParams = this.getMiddlewareParams();

            const chats = await fetchChats(this.caseId, middlewareParams);
            debug.log(this, `Received ${chats.length} chats for caseId: ${this.caseId}`);

            // Store all available chats
            this.availableChats = chats;

            // Update sidebar items
            this.updateSidebarItems();

            // Use the first chat or set to null if no chats are available
            this.chat = chats.length > 0 ? chats[0] : null;

            // Set active item ID if we have a chat
            if (this.chat) {
                this.activeItemId = this.chat.sessionId;
            }

            debug.log(
                this,
                `Selected chat:`,
                this.chat
                    ? `sessionId: ${this.chat.sessionId}, author: ${this.chat.author}, message count: ${this.chat.messages?.length || 0}`
                    : "No chat available"
            );

            // Auto-accept disclaimer if there are more than 2 messages (indicating user interaction)
            if (this.chat?.messages && this.chat.messages.length > 2) {
                this.hasAcceptedDisclaimer = true;
            } else {
                this.hasAcceptedDisclaimer = false;
            }

            // Reset error state and retry count on successful load
            this.error = null;
            this.retryCount = 0;
        } catch (error) {
            console.error("[AiChatWrapper:loadChat] Error loading chat:", error);

            // Handle 404 error by creating a new chat
            if (error instanceof NotFoundError) {
                debug.log(this, "Case not found, attempting to create a new chat");
                try {
                    // Determine if this is a user or technician chat based on the group provided
                    const group = this.chat?.group || "User";
                    const author = this.chat?.author || "User";

                    // Create a new chat
                    const newChat = await addChat(this.caseId, author, group);
                    debug.log(this, "New chat created:", newChat);

                    // Fetch chats again to get the new chat
                    const middlewareParams = this.getMiddlewareParams();
                    const chats = await fetchChats(this.caseId, middlewareParams);

                    // Store all available chats
                    this.availableChats = chats;

                    // Update sidebar items
                    this.updateSidebarItems();

                    this.chat = chats.length > 0 ? chats[0] : newChat;

                    // Set active item ID
                    if (this.chat) {
                        this.activeItemId = this.chat.sessionId;
                    }

                    debug.log(this, "Fetched chat after creation:", this.chat);

                    // Reset error state
                    this.error = null;
                    this.retryCount = 0;
                    return; // Return here to avoid setting loading=false in finally block yet
                } catch (addChatError) {
                    console.error("[AiChatWrapper:loadChat] Error creating new chat:", addChatError);
                    this.error = {
                        errorCode: 500,
                        errorMessage: i18nProvider.t("ai-in-service:error.createChat")
                    };
                }
            } else if (error instanceof NetworkError) {
                this.error = {
                    errorCode: error.code,
                    errorMessage: i18nProvider.t("ai-in-service:error.network")
                };
            } else if (error instanceof ChatServiceError) {
                this.error = {
                    errorCode: error.code,
                    errorMessage: error.message
                };
            } else {
                this.error = {
                    errorCode: 500,
                    errorMessage: i18nProvider.t("ai-in-service:error.unexpected")
                };
            }
            debug.log(this, `Set error state:`, this.error);
        } finally {
            this.loading = false;
        }
    }

    private async loadCaseStatus() {
        if (!this.caseId) {
            console.warn(i18nProvider.t("ai-in-service:message.noCaseId"));
            return;
        }

        debug.log(this, `Loading case status for caseId: ${this.caseId}`);
        try {
            // Pass middleware params to getCaseStatusString
            const middlewareParams = this.getMiddlewareParams();
            const newStatus = await getCaseStatusString(this.caseId, middlewareParams);
            debug.log(this, `Received status: ${newStatus}, current status: ${this.caseStatus}`);

            const isInitialLoad = this.caseStatus === "loading";

            if (newStatus !== this.caseStatus) {
                this.caseStatus = newStatus;
                debug.log(this, `Updated case status to: ${this.caseStatus}`);
                this.caseStatusUpdated.emit(this.caseStatus);

                // Check if case status is closed and set specific error message
                // Only show error popup if status changed to closed during operation (not on initial load)
                if (newStatus === "closed") {
                    // Stop polling when case is closed
                    this.stopCaseStatusSubscription();

                    if (!isInitialLoad) {
                        this.error = {
                            errorCode: 403,
                            errorMessage: i18nProvider.t("ai-in-service:error.caseClosed"),
                            buttonText: "Ok",
                            showIcon: false
                        };
                        debug.log(this, "Set closed case error message");
                    } else {
                        debug.log(this, "Case initially closed - not showing error popup");
                    }
                } else if (this.error?.errorCode === 403 && newStatus !== "closed") {
                    // Clear the closed error if status is no longer closed
                    this.error = null;
                }
            }
        } catch (error) {
            console.error("Error fetching case status:", error);
            debug.log(this, `Error fetching case status: ${error}`);
            // Don't update the main error state for status failures
            // Just update the status to indicate the error
            this.caseStatus = "error";
        } finally {
            // No loading state change needed here, handled by loadChat or init timeout
        }
    }

    private startCaseStatusSubscription() {
        if (!this.caseId) return;

        // Don't start polling if case is already closed
        if (this.caseStatus === "closed") {
            debug.log(this, `Not starting case status subscription - case is already closed`);
            return;
        }

        // Only use debug.log as requested
        debug.log(this, `Starting case status subscription for caseId: ${this.caseId}`);

        // Clear existing interval if any
        if (this.caseStatusInterval) {
            clearInterval(this.caseStatusInterval);
        }

        // Use a shorter interval for testing (5 seconds instead of 30)
        this.caseStatusInterval = setInterval(() => {
            if (!this.isOffline) {
                // Skip polling if already closed
                if (this.caseStatus === "closed") {
                    debug.log(this, `Stopping case status checks - case is now closed`);
                    this.stopCaseStatusSubscription();
                    return;
                }

                debug.log(this, `Interval triggered: checking case status for caseId: ${this.caseId}`);
                void this.loadCaseStatus();
            }
        }, 30000); // Interval set to 30 seconds
    }

    private stopCaseStatusSubscription() {
        if (this.caseStatusInterval) {
            debug.log(this, `Stopping case status subscription for caseId: ${this.caseId}`);
            clearInterval(this.caseStatusInterval);
            this.caseStatusInterval = null;
        }
    }

    private handleDisclaimerSubmitted() {
        this.hasAcceptedDisclaimer = true;
        debug.log(this, "Disclaimer submitted and accepted.");
        // Initialize chat data if needed (ensure messages array exists)
        if (this.chat && !this.chat.messages) {
            this.chat = {
                ...this.chat,
                messages: []
            };
        }
    }

    render() {
        const t = (key: TranslationKey, options: TranslationOptions = { returnObjects: true }) =>
            i18nProvider.t(`ai-in-service:${String(key)}`, {
                ...options,
                interpolation: { escapeValue: false }
            });

        const defaultChat: IChatData = {
            sessionId: `s-${Date.now()}`,
            author: "User",
            group: "User",
            timestamp: new Date().toISOString(),
            messages: []
        };

        // Get disclaimer content and ensure it's an array
        const disclaimerContent = (() => {
            const content = t("legalDisclaimer.content", { returnObjects: true });
            return Array.isArray(content) ? content : [content];
        })();

        // Get checkbox content and ensure it's an array
        const checkboxContent = (() => {
            const content = t("legalDisclaimer.checkboxLabel", { returnObjects: true });
            return Array.isArray(content) ? content : [content];
        })();

        // Determine if the disclaimer should be shown
        const showDisclaimer =
            !this.isServiceApp && // Don't show for service app
            this.initialized && // Only show after init has run (or attempted)
            !this.loading && // Don't show while loading initial chat
            this.chat?.messages && // Requires chat and messages array
            this.chat.messages.length <= 2 && // Only for initial messages
            !this.hasAcceptedDisclaimer; // Only if not accepted

        const isLoading = this.loading || !this.translationsLoaded;

        return (
            <div class="ai-chat-wrapper">
                {!this.isServiceApp && (
                    <auth-handler
                        clientName={authClientName}
                        clientId={this.config.authClientId}
                        issuer={this.config.authIssuer}
                        autoAuth={this.showSidebar}
                        {...(this.debug ? { debug: true } : {})} // Pass debug prop if enabled
                    ></auth-handler>
                )}
                {isLoading && (
                    <div class="loading-state">
                        <ux-loader variant="horizontal"></ux-loader>
                    </div>
                )}
                {!isLoading && (
                    <div class="ai-chat-container">
                        {this.showSidebar && (
                            <sis-sidebar
                                items={this.sidebarItems}
                                activeItemId={this.activeItemId}
                                onSelectItem={(e) => this.handleSidebarItemSelect(e.detail)}
                            />
                        )}
                        <div class="chat-main-content">
                            <ai-chat
                                key={this.chat?.sessionId || "initial-or-loading"}
                                class={showDisclaimer ? "disabled-chat" : ""}
                                token={this.token}
                                data={this.chat || defaultChat} // Pass defaultChat if chat is null
                                caseId={this.caseId}
                                caseStatus={this.caseStatus}
                                locale={this.locale}
                                error={this.error}
                                machineName={this.machineName}
                                machineNumber={this.machineNumber}
                                softwareType={this.softwareType}
                                softwareModule={this.softwareModule}
                                errorDescription={this.errorDescription}
                                errorNumbers={this.errorNumbers}
                                isServiceApp={this.isServiceApp}
                            ></ai-chat>
                            {showDisclaimer && (
                                <div class="disclaimer-overlay">
                                    <legal-disclaimer
                                        headline={t("legalDisclaimer.headline")}
                                        buttonLabel={t("legalDisclaimer.buttonLabel")}
                                        checkboxLabel={checkboxContent}
                                        onDisclaimerSubmitted={() => this.handleDisclaimerSubmitted()}
                                    >
                                        {disclaimerContent.map((content, index) => (
                                            <ux-copy variant="default" strong={index === 1 ? true : undefined} key={index}>
                                                {content}
                                            </ux-copy>
                                        ))}
                                    </legal-disclaimer>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
